import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User,
  Mail,
  MapPin,
  Save,
  LogOut,
  Award,
  BadgeCheck,
  Clock,
  FileText,
  Camera,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { Alert, Spinner } from "../../components/ui";
import BackButton from "../../components/BackButton";
import { ProfileAvatar } from "../../components/ProfilePhoto";
import { INDONESIAN_CITIES } from "../../data/cities";
import EmailVerification from "../../components/EmailVerification";
import AppRating from "../../components/AppRating";
import ReferralCard from "../../components/ReferralCard";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";
import { Link } from "react-router-dom";
import {
  SNK_CHECKBOXES,
  SNK_CONSENT_STATEMENT,
  SNK_VERSION,
  loadSnkConsent,
} from "../../data/snkKyc";

export default function WorkerProfile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [kycFlash, setKycFlash] = useState(false);
  const [displayName, setDisplayName] = useState(user?.profile?.display_name || "");
  const [city, setCity] = useState(user?.profile?.city || "");
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setErr("Format foto harus JPEG, PNG, atau WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Ukuran foto maksimal 5MB.");
      return;
    }
    setPhotoBusy(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("photo", file);
      await api.post("/api/profile/me/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setMsg("Foto profil diperbarui.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Gagal mengunggah foto.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const deletePhoto = async () => {
    if (!window.confirm("Hapus foto profil?")) return;
    setPhotoBusy(true);
    try {
      await api.delete("/api/profile/me/photo");
      await refresh();
      setMsg("Foto profil dihapus.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Gagal menghapus foto.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const [kycData, setKycData] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [showKycForm, setShowKycForm] = useState(false);
  const [kycForm, setKycForm] = useState({
    full_legal_name: "",
    ktp_number: "",
    skck_expiry: "",
  });
  const [ktpFile, setKtpFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [skckFile, setSkckFile] = useState(null);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  // SNK: persetujuan wajib dibaca di /worker/kyc-terms sebelum form dibuka,
  // lalu dicentang ulang di form sebagai bukti eksplisit per submit.
  const [snkConsent, setSnkConsent] = useState(() => loadSnkConsent(user?.id));
  const [snkChecks, setSnkChecks] = useState({ data: false, truth: false });

  // SKCK update state
  const [showSkckUpdate, setShowSkckUpdate] = useState(false);
  const [skckUpdateFile, setSkckUpdateFile] = useState(null);
  const [skckUpdateExpiry, setSkckUpdateExpiry] = useState("");
  const [skckUpdating, setSkckUpdating] = useState(false);

  const tier = user?.profile?.tier || "LITE";
  const kyc = user?.profile?.kyc_status || "PENDING";
  const skckExpired = kycData?.skck_expired || false;
  const isApprovedWithExpiredSkck = kyc === "APPROVED" && skckExpired;
  const needsSkckUpload = kyc === "SKCK_PENDING" || isApprovedWithExpiredSkck;

  const tierConfig = {
    LITE: { label: "LITE", color: "bg-brand-100 text-brand-700" },
    ELITE: { label: "ELITE", color: "bg-brand-600 text-white" },
    PRO: { label: "PRO", color: "bg-success-500 text-white" },
  };
  const tierInfo = tierConfig[tier] || tierConfig.LITE;

  const fetchKycStatus = async () => {
    try {
      const { data } = await api.get("/api/worker/kyc/status");
      setKycData(data);
      cacheSet(cacheKey(user?.id, "kyc-status"), data, 300000);
      if (data.kyc_status) {
        // Sync with profile
      }
    } catch (err) {
      console.error("Failed to load KYC status:", err);
    } finally {
      setKycLoading(false);
    }
  };

  useEffect(() => {
    const cached = cacheGet(cacheKey(user?.id, "kyc-status")).data;
    if (cached) {
      setKycData(cached);
      setKycLoading(false);
    }
    fetchKycStatus();
  }, [user?.id]);

  // Datang dari halaman SNK (state.snkAccepted): buka form otomatis.
  useEffect(() => {
    if (location.state?.snkAccepted) {
      setSnkConsent(loadSnkConsent(user?.id));
      setShowKycForm(true);
      // Bersihkan state agar refresh tidak membuka ulang.
      navigate(location.pathname + (location.hash || ""), { replace: true, state: {} });
    }
  }, [location.state]);

  // Datang dari tur ("Verifikasi Sekarang"): scroll + sorot bagian KYC.
  useEffect(() => {
    if (location.hash === "#kyc") {
      setKycFlash(true);
      const t1 = setTimeout(() => {
        document.getElementById("kyc-section")?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 350);
      const t2 = setTimeout(() => setKycFlash(false), 5000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [location.hash]);

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    setErr("");
    setSaving(true);
    try {
      await api.put("/api/profile/me", { display_name: displayName, city });
      await refresh();
      setMsg("Profil berhasil diperbarui.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKycSubmit = async () => {
    if (!snkChecks.data || !snkChecks.truth) {
      setErr("Centang kedua persetujuan SNK terlebih dahulu sebelum mengirim verifikasi.");
      return;
    }
    setKycSubmitting(true);
    setErr("");
    try {
      const formData = new FormData();
      formData.append("full_legal_name", kycForm.full_legal_name);
      formData.append("ktp_number", kycForm.ktp_number);
      formData.append("ktp_photo", ktpFile);
      formData.append("selfie_photo", selfieFile);
      formData.append("skck_photo", skckFile);
      formData.append("skck_expiry", kycForm.skck_expiry);
      // Bukti persetujuan SNK (UU PDP Pasal 24) — backend memvalidasi & menyimpan.
      formData.append("snk_version", SNK_VERSION);
      formData.append("snk_consent_data", "true");
      formData.append("snk_consent_truth", "true");
      formData.append("snk_statement", SNK_CONSENT_STATEMENT);

      await api.post("/api/worker/kyc/submit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowKycForm(false);
      setKycForm({ full_legal_name: "", ktp_number: "", skck_expiry: "" });
      setKtpFile(null);
      setSelfieFile(null);
      setSkckFile(null);
      setSnkChecks({ data: false, truth: false });
      setSnkConsent(loadSnkConsent(user?.id));
      await fetchKycStatus();
      await refresh();
      setMsg("KYC berhasil diajukan. Menunggu review admin.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setKycSubmitting(false);
    }
  };

  const handleSkckUpdate = async () => {
    setSkckUpdating(true);
    setErr("");
    try {
      const formData = new FormData();
      formData.append("skck_photo", skckUpdateFile);
      formData.append("skck_expiry", skckUpdateExpiry);

      await api.post("/api/worker/kyc/update-skck", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowSkckUpdate(false);
      setSkckUpdateFile(null);
      setSkckUpdateExpiry("");
      await fetchKycStatus();
      await refresh();
      setMsg("SKCK berhasil diperbarui. Menunggu review admin.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSkckUpdating(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const kycStatusLabel = {
    PENDING: { text: "Belum Diajukan", color: "bg-gray-100 text-gray-600", icon: Clock },
    SUBMITTED: { text: "Diajukan", color: "bg-amber-50 text-amber-600", icon: Clock },
    UNDER_REVIEW: { text: "Sedang Direview", color: "bg-blue-50 text-blue-600", icon: Clock },
    SKCK_PENDING: { text: "SKCK Belum Terverifikasi", color: "bg-amber-50 text-amber-600", icon: AlertTriangle },
    APPROVED: { text: "Terverifikasi", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
    REJECTED: { text: "Ditolak", color: "bg-red-50 text-red-600", icon: XCircle },
  };

  const kycInfo = kycStatusLabel[kyc] || kycStatusLabel.PENDING;
  const KycIcon = kycInfo.icon;
  const canSubmitKyc = ["PENDING", "REJECTED"].includes(kyc);

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container max-w-lg space-y-6">
        <BackButton to="/worker" />
        <div>
          <h1 className="text-brand-800">Profil Saya</h1>
          <p className="text-brand-400 mt-1">Kelola informasi pekerja Anda</p>
        </div>

        {/* Avatar + Info */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <ProfileAvatar
              userId={user?.id}
              photoUrl={user?.profile?.profile_photo_url}
              name={displayName}
              onSelect={uploadPhoto}
              inputTestId="worker-profile-photo-input"
              disabled={photoBusy}
            />
            <div>
              <p className="font-bold text-brand-800 text-lg">{displayName || "Pekerja"}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`chip ${tierInfo.color}`}>
                  <Award className="h-4 w-4" /> Tier {tierInfo.label}
                </span>
                <span className={`chip ${kycInfo.color}`}>
                  <KycIcon className="h-4 w-4" /> {kycInfo.text}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mb-6">
            <label className="btn-ghost text-sm cursor-pointer">
              {photoBusy ? "Mengunggah..." : "Ganti foto"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                disabled={photoBusy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  uploadPhoto(f);
                }}
                data-testid="worker-profile-photo-input-alt"
              />
            </label>
            {user?.profile?.profile_photo_url && (
              <button type="button" onClick={deletePhoto} disabled={photoBusy} className="btn-ghost text-sm text-red-600">
                Hapus foto
              </button>
            )}
          </div>

          <form onSubmit={save} className="space-y-4" data-testid="worker-profile-form">
            {msg && <Alert kind="success" testid="worker-profile-success">{msg}</Alert>}
            {err && <Alert testid="worker-profile-error">{err}</Alert>}

            <div>
              <label className="field-label">Nama tampilan</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  className="field-input pl-12"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Nama Anda"
                  data-testid="worker-profile-display-name"
                />
              </div>
            </div>

            <div>
              <label className="field-label">Kota domisili</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  className="field-input pl-12"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Contoh: Jakarta"
                  list="worker-city-list"
                  autoComplete="off"
                  data-testid="worker-profile-city"
                />
              </div>
              <datalist id="worker-city-list">
                {INDONESIAN_CITIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="text-xs text-brand-400 mt-1">Dipakai untuk mencocokkan pelanggan terdekat.</p>
            </div>

            <div>
              <label className="field-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  className="field-input pl-12 bg-brand-50"
                  value={user?.email || ""}
                  disabled
                />
              </div>
            </div>

            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="worker-profile-save">
              <Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </form>
        </div>

        {/* Email Verification */}
        <div className="card p-6">
          <h2 className="text-brand-800 mb-3">Verifikasi Email</h2>
          <EmailVerification
            emailVerified={user?.email_verified}
            onVerified={() => refresh()}
          />
        </div>

        {/* KYC Section */}
        <div
          id="kyc-section"
          data-testid="kyc-section"
          className={`card p-6 transition-all ${kycFlash ? "ring-4 ring-amber-400 border-amber-400 animate-pulse" : ""}`}
        >
          <h2 className="text-brand-800">Verifikasi Identitas (KYC) {kycFlash && <span>👆 di sini!</span>}</h2>
          <p className="text-brand-400 mt-1 text-sm">Unggah dokumen untuk memverifikasi identitas Anda</p>

          {/* KYC Status */}
          <div className="mt-4 flex items-center justify-between rounded-2xl bg-brand-50 px-4 py-4">
            <div className="flex items-center gap-3">
              <KycIcon className={`h-6 w-6 ${kyc === "APPROVED" ? "text-success-500" : "text-brand-300"}`} />
              <div>
                <p className="font-bold text-brand-800">Status Verifikasi</p>
                <p className="text-sm text-brand-500">{kycInfo.text}</p>
              </div>
            </div>
            <span className={`chip ${kycInfo.color}`}>{kycInfo.text}</span>
          </div>

          {/* KYC Submission Detail */}
          {kycLoading ? (
            <div className="mt-4"><Spinner label="Memuat status KYC..." /></div>
          ) : kycData?.submission ? (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-brand-600">
                <FileText className="h-4 w-4" />
                <span>Nama: {kycData.submission.full_legal_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-brand-600">
                <FileText className="h-4 w-4" />
                <span>No. KTP: {kycData.submission.ktp_number}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-brand-600">
                <FileText className="h-4 w-4" />
                <span>SKCK: {kycData.submission.has_skck ? "✓ Diunggah" : "✗ Belum diunggah"}</span>
              </div>
              {kycData.submission.skck_expiry && (
                <div className="flex items-center gap-2 text-sm text-brand-600">
                  <FileText className="h-4 w-4" />
                  <span>Kadaluarsa SKCK: {new Date(kycData.submission.skck_expiry).toLocaleDateString("id-ID")}</span>
                </div>
              )}
              {kycData.submission.review_note && (
                <div className="flex items-start gap-2 text-sm text-brand-600 bg-brand-50 rounded-xl p-3 mt-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Catatan reviewer: {kycData.submission.review_note}</span>
                </div>
              )}
            </div>
          ) : null}

          {/* SKCK Upload Prompt (SKCK_PENDING atau APPROVED+expired) */}
          {needsSkckUpload && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="skck-upload-prompt">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-amber-700">
                    {kyc === "SKCK_PENDING" ? "Lengkapi SKCK" : "SKCK Kadaluarsa"}
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    {kyc === "SKCK_PENDING"
                      ? "Verifikasi KTP sudah disetujui. Silakan unggah SKCK untuk menyelesaikan verifikasi."
                      : "SKCK Anda sudah kadaluarsa. Silakan unggah SKCK terbaru untuk mempertahankan status terverifikasi."}
                  </p>

                  {!showSkckUpdate ? (
                    <button
                      onClick={() => setShowSkckUpdate(true)}
                      className="btn-primary !bg-amber-500 hover:!bg-amber-600 mt-3"
                      data-testid="skck-update-btn"
                    >
                      <RefreshCw className="h-4 w-4" />
                      {kyc === "SKCK_PENDING" ? "Unggah SKCK" : "Perbarui SKCK"}
                    </button>
                  ) : (
                    <div className="space-y-3 mt-3 pt-3 border-t border-amber-200">
                      <div>
                        <label className="text-sm text-amber-700">Foto SKCK</label>
                        <div className="relative">
                          <Camera className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-400" />
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => setSkckUpdateFile(e.target.files?.[0] || null)}
                            className="w-full rounded-xl border-2 border-amber-200 pl-9 pr-3 py-2.5 text-sm mt-1 focus:border-amber-400 focus:outline-none file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-amber-100 file:text-amber-700 file:font-medium file:text-sm"
                            data-testid="skck-update-file"
                          />
                        </div>
                        {skckUpdateFile && (
                          <p className="text-xs text-amber-600 mt-1">{skckUpdateFile.name} ({(skckUpdateFile.size / 1024).toFixed(0)} KB)</p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm text-amber-700">Tanggal Kadaluarsa</label>
                        <input
                          type="date"
                          value={skckUpdateExpiry}
                          onChange={(e) => setSkckUpdateExpiry(e.target.value)}
                          className="w-full rounded-xl border-2 border-amber-200 px-3 py-2.5 text-sm mt-1 focus:border-amber-400 focus:outline-none"
                          data-testid="skck-update-expiry"
                        />
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowSkckUpdate(false); setSkckUpdateFile(null); setSkckUpdateExpiry(""); }}
                          className="btn-ghost flex-1 !py-2"
                        >
                          Batal
                        </button>
                        <button
                          onClick={handleSkckUpdate}
                          disabled={skckUpdating || !skckUpdateFile || !skckUpdateExpiry}
                          className="btn-primary flex-1 !py-2 !bg-amber-500 hover:!bg-amber-600"
                          data-testid="skck-update-submit"
                        >
                          {skckUpdating ? "Mengirim..." : "Kirim SKCK"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* KYC Action — wajib baca SNK dulu */}
          {canSubmitKyc && (
            <div className="mt-4">
              {!showKycForm ? (
                <div className="space-y-2">
                  <button
                    onClick={() => navigate("/worker/kyc-terms")}
                    className="btn-primary w-full"
                    data-testid="kyc-submit-btn"
                  >
                    <BadgeCheck className="h-4 w-4" /> Baca SNK & Ajukan Verifikasi KYC
                  </button>
                  <p className="text-xs text-brand-400 text-center">
                    Anda akan membaca Syarat & Ketentuan KYC (v{SNK_VERSION}) dan mencentang persetujuan sebelum
                    mengunggah dokumen. {snkConsent ? "Anda sudah pernah menyetujui." : ""}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 pt-2 border-t border-brand-50">
                  <div className="flex items-center justify-between mt-3">
                    <p className="font-bold text-brand-800 text-sm">Formulir KYC</p>
                    <Link
                      to="/worker/kyc-terms"
                      className="text-xs text-brand-600 underline"
                      data-testid="kyc-reread-snk"
                    >
                      Baca ulang SNK v{SNK_VERSION}
                    </Link>
                  </div>

                  <div>
                    <label className="text-sm text-brand-500">Nama Lengkap (sesuai KTP)</label>
                    <input
                      type="text"
                      value={kycForm.full_legal_name}
                      onChange={(e) => setKycForm({ ...kycForm, full_legal_name: e.target.value })}
                      placeholder="Nama lengkap"
                      className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none"
                      data-testid="kyc-full-name"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-brand-500">Nomor KTP</label>
                    <input
                      type="text"
                      value={kycForm.ktp_number}
                      onChange={(e) => setKycForm({ ...kycForm, ktp_number: e.target.value })}
                      placeholder="16 digit nomor KTP"
                      className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none"
                      data-testid="kyc-ktp-number"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-brand-500">Foto KTP</label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-300" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => setKtpFile(e.target.files?.[0] || null)}
                        className="w-full rounded-xl border-2 border-brand-100 pl-9 pr-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-brand-100 file:text-brand-700 file:font-medium file:text-sm"
                        data-testid="kyc-ktp-photo"
                      />
                    </div>
                    {ktpFile && (
                      <p className="text-xs text-success-600 mt-1">{ktpFile.name} ({(ktpFile.size / 1024).toFixed(0)} KB)</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-brand-500">Foto Selfie + KTP</label>
                    <div className="relative">
                      <Camera className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-300" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => setSelfieFile(e.target.files?.[0] || null)}
                        className="w-full rounded-xl border-2 border-brand-100 pl-9 pr-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-brand-100 file:text-brand-700 file:font-medium file:text-sm"
                        data-testid="kyc-selfie-photo"
                      />
                    </div>
                    {selfieFile && (
                      <p className="text-xs text-success-600 mt-1">{selfieFile.name} ({(selfieFile.size / 1024).toFixed(0)} KB)</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-brand-500">Foto SKCK</label>
                    <div className="relative">
                      <Camera className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-300" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => setSkckFile(e.target.files?.[0] || null)}
                        className="w-full rounded-xl border-2 border-brand-100 pl-9 pr-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-brand-100 file:text-brand-700 file:font-medium file:text-sm"
                        data-testid="kyc-skck-photo"
                      />
                    </div>
                    {skckFile && (
                      <p className="text-xs text-success-600 mt-1">{skckFile.name} ({(skckFile.size / 1024).toFixed(0)} KB)</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-brand-500">Tanggal Kadaluarsa SKCK</label>
                    <input
                      type="date"
                      value={kycForm.skck_expiry}
                      onChange={(e) => setKycForm({ ...kycForm, skck_expiry: e.target.value })}
                      className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none"
                      data-testid="kyc-skck-expiry"
                    />
                  </div>

                  {/* Persetujuan SNK — wajib centang ulang per submit */}
                  <div className="rounded-2xl border-2 border-brand-100 p-3 space-y-2 bg-brand-50/50" data-testid="kyc-snk-box">
                    <p className="text-xs font-bold text-brand-800">
                      Persetujuan SNK v{SNK_VERSION} (wajib)
                    </p>
                    {SNK_CHECKBOXES.map((c) => (
                      <label key={c.id} className="flex items-start gap-2 text-xs text-brand-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={snkChecks[c.id]}
                          onChange={() => setSnkChecks((s) => ({ ...s, [c.id]: !s[c.id] }))}
                          className="mt-0.5 h-4 w-4 accent-emerald-600"
                          data-testid={`kyc-snk-check-${c.id}`}
                        />
                        <span>{c.label}</span>
                      </label>
                    ))}
                    <p className="text-[11px] text-brand-400 leading-relaxed">
                      “{SNK_CONSENT_STATEMENT.slice(0, 160)}…” —{" "}
                      <Link to="/worker/kyc-terms" className="underline">
                        baca selengkapnya
                      </Link>
                    </p>
                  </div>

                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={() => { setShowKycForm(false); setKtpFile(null); setSelfieFile(null); setSkckFile(null); }}
                      className="btn-ghost flex-1"
                    >
                      Batal
                    </button>
                    <button
                      onClick={handleKycSubmit}
                      disabled={kycSubmitting || !kycForm.full_legal_name || !kycForm.ktp_number || !ktpFile || !selfieFile || !skckFile || !kycForm.skck_expiry || !snkChecks.data || !snkChecks.truth}
                      className="btn-primary flex-1"
                      data-testid="kyc-confirm-submit"
                    >
                      {kycSubmitting ? "Mengirim..." : "Kirim Verifikasi"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {kyc === "SUBMITTED" && (
            <div className="mt-4 flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-xl p-3">
              <Clock className="h-4 w-4" />
              <span>Verifikasi sedang menunggu review admin.</span>
            </div>
          )}

          {kyc === "UNDER_REVIEW" && (
            <div className="mt-4 flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-xl p-3">
              <Clock className="h-4 w-4" />
              <span>Verifikasi sedang dalam proses review oleh admin KYC.</span>
            </div>
          )}
        </div>

        {/* App Rating */}
        <AppRating userId={user?.id} />

        {/* Referral */}
        <ReferralCard />

        {/* Logout */}
        <button onClick={doLogout} className="btn-ghost w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300" data-testid="worker-profile-logout">
          <LogOut className="h-4 w-4" /> Keluar dari Akun
        </button>
      </div>
    </div>
  );
}
